import React, { useState, useEffect } from 'react';
import { TicketData, VenueData, TicketGroupData } from '../types';
import { getOrganizerCurrency } from '../../../../utils/currency';
import { Ticket, Pencil, Trash2, MoreVertical, GripVertical } from 'lucide-react';

interface TicketListProps {
  tickets: TicketData[];
  hasMultipleVenues?: boolean;
  venues?: VenueData[];
  hasMultipleTicketGroups?: boolean;
  ticketGroups?: TicketGroupData[];
  onTicketToggle: (ticketId: string, isActive: boolean) => void;
  onEditTicket?: (ticket: TicketData) => void;
  onDeleteTicket?: (ticketId: string) => void;
  onReorderTickets?: (reorderedTickets: TicketData[]) => void;
}

const TicketList: React.FC<TicketListProps> = ({
  tickets,
  hasMultipleVenues = false,
  venues = [],
  hasMultipleTicketGroups = false,
  ticketGroups = [],
  onTicketToggle,
  onEditTicket,
  onDeleteTicket,
  onReorderTickets
}) => {
  const currencySymbol = getOrganizerCurrency().symbol;
  const [activeMenuTicketId, setActiveMenuTicketId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleClickOutside = () => setActiveMenuTicketId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isDraggable = Boolean(onReorderTickets && tickets.length > 1);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== targetIndex) {
      const updated = Array.from(tickets);
      const [movedItem] = updated.splice(draggedIndex, 1);
      updated.splice(targetIndex, 0, movedItem);
      const reordered = updated.map((t, idx) => ({ ...t, ticketOrder: idx + 1 }));
      onReorderTickets?.(reordered);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Helper function to get venue name by ID
  const getVenueName = (venueId: string) => {
    const venue = venues.find(v => v.id === venueId);
    return venue ? venue.name : 'Unknown Venue';
  };

  // Helper function to get ticket group name by ID
  const getTicketGroupName = (ticketGroupId: string) => {
    const ticketGroup = ticketGroups.find(tg => String(tg.id) === String(ticketGroupId));
    return ticketGroup ? ticketGroup.name : 'Unknown Group';
  };

  if (tickets.length === 0) {
    return (
      <div style={{ padding: "32px 16px", textAlign: "center", borderRadius: 14, background: 'var(--v3-bg-subtle, rgba(255,255,255,0.02))', border: '1px dashed var(--v3-border-medium, rgba(255,255,255,0.08))' }}>
        <Ticket size={28} color="var(--v3-text-faint, rgba(255,255,255,0.2))" style={{ margin: "0 auto 8px auto", display: "block" }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main, #fff)' }}>No tickets created yet</div>
        <p style={{ fontSize: 11, color: 'var(--v3-text-muted, rgba(255,255,255,0.4))', marginTop: 4, marginBottom: 0 }}>You have not created a ticket yet. Please click above to create your event ticket.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
      {tickets.map((ticket, index) => (
        <div
          key={ticket.id}
          draggable={isDraggable}
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          style={{
            padding: 16,
            borderRadius: 14,
            background: draggedIndex === index
              ? "rgba(238, 63, 124, 0.08)"
              : dragOverIndex === index
              ? "rgba(255, 255, 255, 0.06)"
              : "var(--v3-bg-subtle, rgba(255,255,255,0.03))",
            border: draggedIndex === index
              ? "1px dashed #EE3F7C"
              : dragOverIndex === index
              ? "1px solid #EE3F7C"
              : "1px solid var(--v3-border-subtle, rgba(255,255,255,0.07))",
            opacity: draggedIndex === index ? 0.45 : 1,
            transform: draggedIndex === index ? "scale(0.98)" : dragOverIndex === index ? "translateY(-2px)" : "none",
            boxShadow: dragOverIndex === index ? "0 8px 20px rgba(238, 63, 124, 0.2)" : "none",
            transition: "all 0.2s cubic-bezier(0.2, 0, 0, 1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            cursor: isDraggable ? "grab" : "default",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 240 }}>
            {isDraggable && (
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                style={{
                  cursor: "grab",
                  color: "rgba(255, 255, 255, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "6px 2px",
                  borderRadius: "6px",
                  transition: "color 0.15s, background 0.15s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#ffffff")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255, 255, 255, 0.4)")}
                title="Drag to reorder ticket"
              >
                <GripVertical size={16} />
              </div>
            )}

            <div
              style={{
                width: 96,
                height: 40,
                borderRadius: 10,
                background: ticket.price === 0
                  ? "linear-gradient(135deg, #059669, #34D399)"
                  : "linear-gradient(135deg, #EE3F7C, #F79B59)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                boxShadow: ticket.price === 0
                  ? "0 4px 14px rgba(52, 211, 153, 0.25)"
                  : "0 4px 14px rgba(238, 63, 124, 0.3)",
                flexShrink: 0,
              }}
            >
              {ticket.price === 0 ? "FREE" : `${currencySymbol}${ticket.price.toFixed(2)}`}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--v3-text-main, #fff)" }}>
                  {ticket.name || (ticket as any).ticketType || "Untitled Ticket"}
                </span>

                {ticket.isOfferActive && ticket.previousAmount && (
                  <span style={{ fontSize: 12, textDecoration: "line-through", color: "var(--v3-text-muted)" }}>
                    {currencySymbol}{ticket.previousAmount.toFixed(2)}
                  </span>
                )}

                {ticket.isOfferActive && (
                  <span className="badge bg-success-subtle text-success border border-success-subtle fs-12 px-2 py-1">
                    <i className="fa-solid fa-tag me-1"></i>Offer Active
                  </span>
                )}

                <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 6px", borderRadius: 4, background: "var(--v3-border-subtle, rgba(255,255,255,0.06))", color: "var(--v3-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
                  {ticket.type === 'group' ? 'Group Ticket' : 'Single Ticket'}
                </span>

                {hasMultipleVenues && ticket.venueId && (
                  <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 6px", borderRadius: 4, background: "var(--v3-border-subtle, rgba(255,255,255,0.06))", color: "var(--v3-text-muted)" }}>
                    Venue: {getVenueName(ticket.venueId)}
                  </span>
                )}

                {hasMultipleTicketGroups && ticket.ticketGroupId && (
                  <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 6px", borderRadius: 4, background: "var(--v3-border-subtle, rgba(255,255,255,0.06))", color: "var(--v3-text-muted)" }}>
                    Group: {getTicketGroupName(ticket.ticketGroupId)}
                  </span>
                )}
              </div>

              <div style={{ fontSize: 11, color: "var(--v3-text-muted, rgba(255,255,255,0.45))", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <span>
                  Max Allowed Count: <strong>
                    {ticket.totalTickets === null || ticket.totalTickets === undefined || ticket.totalTickets === -1
                      ? 'Unlimited'
                      : (ticket.isGroupTicket || ticket.type === 'group') && ticket.groupTicketCount
                      ? (ticket.totalTickets * ticket.groupTicketCount).toString()
                      : ticket.totalTickets}
                  </strong>
                </span>
                {ticket.description && (
                  <span>· {ticket.description}</span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              className={`v3-toggle ${ticket.isActive ? "on" : "off"}`}
              onClick={() => onTicketToggle(ticket.id, !ticket.isActive)}
            >
              <span className="v3-toggle-knob" />
            </button>

            {(onEditTicket || onDeleteTicket) && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveMenuTicketId(activeMenuTicketId === ticket.id ? null : ticket.id);
                  }}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: activeMenuTicketId === ticket.id ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    color: "rgba(255, 255, 255, 0.8)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  title="More actions"
                >
                  <MoreVertical size={16} />
                </button>

                {activeMenuTicketId === ticket.id && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 40,
                      zIndex: 100,
                      minWidth: 140,
                      padding: "6px",
                      borderRadius: 12,
                      background: "#181320",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {onEditTicket && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuTicketId(null);
                          onEditTicket(ticket);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "transparent",
                          border: "none",
                          color: "#ffffff",
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <Pencil size={13} color="rgba(255, 255, 255, 0.7)" /> Edit Ticket
                      </button>
                    )}

                    {onDeleteTicket && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuTicketId(null);
                          onDeleteTicket(ticket.id);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: 8,
                          background: "transparent",
                          border: "none",
                          color: "#FF5D73",
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign: "left",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          transition: "background 0.15s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255, 93, 115, 0.12)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <Trash2 size={13} color="#FF5D73" /> Delete Ticket
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TicketList;
